import { useLocale } from "../../../hooks/useLocale";
import { msg } from "../../../locales/translate";
import { DateTimeTooltip } from "src/view/conponents/DateTimeTooltip";
import { formatDisplayDate, type ServerDateValue } from "src/utils/dateTime";
import { Box, HBox, VBox } from "@ui/box/Box";
import { Error } from "@ui/error/Error";
import { createName } from "@ui/form/FormParser";
import { IconArrowRight } from "@ui/icons/IconArrowRight";
import { Page } from "@ui/page/Page";
import Spacer from "@ui/spacer/Spacer";
import { Typo } from "@ui/text/Typo";
import { CountriesData } from "@ui/utils/CountriesData";
import { useEffect, useState } from "react";
import { GD } from "src/GD";
import { IconCalendar } from "src/assets/icon_calendar";
import { IconGlobe } from "src/assets/icon_globe";
import { IconId } from "src/assets/icon_id";
import { IconRegNumber } from "src/assets/icon_reg_number";
import { IconSettings } from "src/assets/icon_settings";
import { IClient } from "src/services/ClientService";
import { useCurrentPath, useRouter } from "src/services/Router";
import { S_ADD_CLIENT_SUCCESS } from "src/view/popup/AddClientPopup";
import { S_CLIENT_SETTINGS_POPUP } from "src/view/popup/ClientSettingsPopup";
import { ClientPageContent } from "./subpages/ClientPageContent";
import { IconEdit } from "@ui/icons/IconEdit";
import { hasAnyRole, hasPermission } from "src/permissions";
import { cancelFailedClientDashboardComparison } from "../../statView/clientDashboardComparison";
import { useClientPageData } from "./useClientPageData";
import { useTranslation } from "react-i18next";

export interface IClientPageProps {}

const parseClientStatusPayload = (value: unknown): { old_status?: string; new_status?: string } => {
	let parsed = value;
	if (typeof value === "string") {
		try {
			parsed = JSON.parse(value);
		} catch {
			return {};
		}
	}

	if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
	const payload = parsed as Record<string, unknown>;
	return {
		old_status: typeof payload.old_status === "string" ? payload.old_status : undefined,
		new_status: typeof payload.new_status === "string" ? payload.new_status : undefined,
	};
};

export const ClientPage = (_props: IClientPageProps) => {
	useLocale();
	const router = useRouter();
	const path = useCurrentPath();
	const clientID = path?.currentParams.cid ?? router.currentParams.cid;
	const [project] = GD.B_SELECTED_PROJECT.use();
	const [user] = GD.REQ_PROFILE.useValue(undefined, undefined, [project]);

	// Keep the dashboard mounted between clients, while isolating accounts/projects.
	return project && user ? <ClientPageForClient key={`${project.id}:${user.id}`} clientID={clientID} projectId={project.id} /> : null;
};

const ClientPageForClient = ({ clientID, projectId }: { clientID: string; projectId: number }) => {
	useLocale();
	const { t } = useTranslation();
	const [headerHeight] = GD.B_HEADER_HEIGHT.use(undefined, undefined, []);
	const { client, error, busy, reload: updateClient, update } = useClientPageData(Number(clientID), projectId);
	const switching = !!client && client.id !== Number(clientID);

	useEffect(() => {
		if (error) cancelFailedClientDashboardComparison(Number(clientID));
	}, [clientID, error]);

	S_ADD_CLIENT_SUCCESS.use([clientID, client], data => {
		if (data && client?.id === data.id && data.id === Number(clientID)) {
			update({ ...client, ...data });
		}
	});

	return (
		<Page>
			{error && <Error error={error} />}
			{error && <button type="button" onClick={updateClient}>{t("common.retry")}</button>}
			{switching && busy && <div role="status" style={{ position: "fixed", top: (headerHeight ?? 60) + 8, right: 24, zIndex: 30, padding: "8px 14px", background: "#fff", border: "1px solid #dbe1e9", borderRadius: 8, boxShadow: "0 2px 8px #0001" }}>{t("common.loading")}</div>}
			<div aria-busy={busy} ref={node => { if (node) node.inert = switching; }} style={{ opacity: switching ? 0.45 : 1, transition: "opacity .15s" }}>

				{client && (
					<Box style={{ background: getMerchantGradient(client.id) }}
						sx={{ padding: "50px 20px 20px 20px", color: "#ffffff", borderRadius: "6px" }}>
						<ClientInfo key={client.id} client={client} onUpdate={updateClient} />
					</Box>
				)}
				<Spacer size="20px" />
				<ClientPageContent client={client} />
			</div>
		</Page>
	);
};

export interface IClientInfoProps {
	client: IClient;
	onUpdate: () => void;
}

export const ClientInfo = ({ client, onUpdate }: IClientInfoProps) => {
	useLocale();
	//!TODO: hook?
	const [project] = GD.B_SELECTED_PROJECT.use();
	const [user] = GD.REQ_PROFILE.useValue(undefined, undefined, [project]);

	let showAdminControls: boolean = false;
	let canManageSettings: boolean = false;
	if (project && user) {
		const userProject = user.projects.find(val => val.id === project.id);
		if (userProject) {
			showAdminControls = hasAnyRole(userProject.roles, ["admin"]);
			canManageSettings = hasPermission(userProject.roles, "clients.manage_settings");
		}
	}

	const updateClient = () => {
		onUpdate();
	};

	return (
		<VBox>
			<HBox gap="10px" sx={{ alignItems: "start" }}>
				<VBox gap="10px" sx={{ width: "100%" }}>
					<HBox gap="10px" sx={{ alignItems: "start" }}>
						<HBox gap="8px" sx={{ alignItems: "center", width: "100%", flexWrap: "wrap" }}>
							<Typo sx={{ color: "#FFF", fontSize: "26px", fontWeight: "600" }}>{client.name ? client.name : msg("clientNameEmpty")}</Typo>

							<Spacer grow minHeight="20px" />

							<HBox gap="8px">
								<ClientStatusBadge
									client={client}
									showAdminControls={showAdminControls}
									onChangeStatus={() => {
										S_CLIENT_SETTINGS_POPUP.invoke({ client, section: "status", onUpdate: updateClient });
									}}
								/>

								{canManageSettings && (
									<HBox
										gap="10px"
										onClick={() => {
											S_CLIENT_SETTINGS_POPUP.invoke({ client, section: "rolling", onUpdate: updateClient });
										}}
										sx={{
											padding: "8px 16px",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											height: "34px",
											backgroundColor: getStatusColor(client.status),
											borderRadius: "6px",
											cursor: "pointer",
											color: "#fff",
											transition: "background-color 0.2s",
											"&:hover": {
												backgroundColor: getStatusColor(client.status) + "cc",
											},
										}}>
										<Typo
											sx={{
												color: "#fff",
												fontWeight: "600",
												fontSize: "14px",
												whiteSpace: "nowrap",
											}}>{msg("settings")}</Typo>

										<IconSettings color="#fff" />
									</HBox>
								)}
							</HBox>
						</HBox>
					</HBox>

					<Box
						sx={{
							width: "100%",
							display: "grid",
							gridTemplateColumns: "repeat(auto-fit, minmax(clamp(160px, calc((660px - 100%) * 9999), calc(50% - 0.5px)), 1fr))",
							gap: "1px",
							borderRadius: "4px",
							overflow: "hidden",
						}}>
						<ClientInfoCell label={msg("id_3843971d")} value={String(client.id ?? 0)} icon={<IconId />} copyable />
						<ClientInfoCell
							label={msg("country")}
							value={client.country ? (CountriesData.countries.find(c => c.alpha2 === client.country)?.name ?? "—") : "—"}
							icon={<IconGlobe />}
						/>
						<ClientInfoCell label={msg("regNumber")} value={client.registration_number || "—"} icon={<IconRegNumber />} copyable />
						<ClientInfoCell
							label={msg("created")}
							value={formatDisplayDate(client.created_at)}
							dateTime={client.created_at}
							icon={<IconCalendar />}
						/>
					</Box>

					{/* <Timeline client={client} /> */}
				</VBox>
			</HBox>
		</VBox>
	);
};

// Client info cell for the admin details grid
const ClientInfoCell = ({ label, value, icon, copyable, dateTime }: { label: string; value: string; icon?: React.ReactNode; copyable?: boolean; dateTime?: ServerDateValue }) => {
	useLocale();
	//const theme = useTheme() as ITheme;
	const [copied, setCopied] = useState(false);

	const handleCopy = () => {
		if (!copyable || value === "—") return;
		navigator.clipboard.writeText(value);
		setCopied(true);
		setTimeout(() => setCopied(false), 1500);
	};

	return (
		<VBox
			onClick={copyable ? handleCopy : undefined}
			gap="6px"
			sx={{
				padding: "14px 18px",
				//backgroundColor: "#4b3fb576",
				backgroundColor: "#0a082285",
				cursor: copyable && value !== "—" ? "pointer" : "default",
				transition: "background-color 0.15s",
				"&:hover": copyable && value !== "—" ? { backgroundColor: "#0000004a" } : {},
			}}>
			<HBox gap="6px" sx={{ alignItems: "center" }}>
				{icon}
				<Typo sx={{ color: "#d9dfe9", fontSize: "11px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</Typo>
				{copyable && value !== "—" && (
					<Typo sx={{ color: copied ? "#4CAF50" : "#bbb", fontSize: "10px", marginLeft: "auto", transition: "color 0.2s" }}>
						{copied ? "✓" : msg("copy_6f5a6034") /*это охуенчик*/}
					</Typo>
				)}
			</HBox>
			<Typo sx={{ color: "#FFF", fontSize: "15px", fontWeight: "600" }}><DateTimeTooltip value={dateTime}>{value}</DateTimeTooltip></Typo>
		</VBox>
	);
};

// Status badge component with colors, tooltip on hover, and click action for admins
export interface IClientStatusBadgeProps {
	client: IClient;
	showAdminControls: boolean;
	onChangeStatus: () => void;
}

const getStatusColor = (status: string): string => {
	const statusLower = status?.toLowerCase()?.trim() || "";

	if (statusLower.includes("active") || statusLower.includes("approved") || statusLower.includes("live")) {
		return "#4CAF50"; // green
	}
	if (statusLower.includes("pending") || statusLower.includes("review") || statusLower.includes("waiting")) {
		return "#FF9800"; // orange
	}
	if (statusLower.includes("reject") || statusLower.includes("declined") || statusLower.includes("blocked") || statusLower.includes("suspended")) {
		return "#F44336"; // red
	}
	if (statusLower.includes("draft") || statusLower.includes("new") || statusLower.includes("created")) {
		return "#2196F3"; // blue
	}
	if (statusLower.includes("closed") || statusLower.includes("archived") || statusLower.includes("inactive")) {
		return "#9E9E9E"; // gray
	}
	return "#607D8B"; // default blue-gray
};

export const ClientStatusBadge = ({ client, showAdminControls, onChangeStatus }: IClientStatusBadgeProps) => {
	useLocale();
	const [showTooltip, setShowTooltip] = useState<boolean>(false);
	const [history, _req, _hreset, _hBusy] = GD.REQ_API.useRequest(undefined, {
		method: "events/list",
		data: {
			target_id: client.id,
			target_type: "client",
			action: "client_status_change",
			limit: 5,
		},
	});

	const statusColor = getStatusColor(client.status);
	const historyItems = Array.isArray(history?.data) ? history.data : [];

	return (
		<div
			style={{ position: "relative", height: "34px" }}
			onMouseEnter={() => showAdminControls && setShowTooltip(true)}
			onMouseLeave={() => setShowTooltip(false)}>
			{/* Status Badge with integrated edit button */}
			<HBox
				style={{ backgroundColor: statusColor }}
				sx={{
					alignItems: "stretch",
					borderRadius: "6px",
					height: "100%",
					//borderRadius: "12px", // у нас то 6 то 12 то еще хз сколько, на каждую "выдумку" создается новый css и мы попадаем на "больше 200 классов"
					overflow: "hidden",
				}}>
				<Box
					sx={{
						padding: "8px 16px",
						display: "flex",
						alignItems: "center",
					}}>
					<Typo
						sx={{
							color: "#fff",
							fontWeight: "600",
							fontSize: "14px",
							whiteSpace: "nowrap",
						}}>
						{msg("status_4889951f") + createName(client.status) || msg("unknown")}
					</Typo>
				</Box>

				{/* Edit icon button - only for admin users */}
				{showAdminControls && (
					<Box
						onClick={onChangeStatus}
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							padding: "0 10px",
							backgroundColor: "rgba(0,0,0,0.15)",
							cursor: "pointer",
							color: "#fff",
							transition: "background-color 0.2s",
							"&:hover": {
								backgroundColor: "rgba(0,0,0,0.3)",
							},
						}}>
						<IconEdit color="#fff" />
					</Box>
				)}
			</HBox>

			{/* Tooltip with status history */}
			{showTooltip && historyItems.length > 0 && (
				<Box
					sx={{
						position: "absolute",
						top: "100%",
						right: 0,
						marginTop: "8px",
						backgroundColor: "rgba(0, 0, 0, 0.9)",
						borderRadius: "12px",
						padding: "12px",
						minWidth: "280px",
						zIndex: 1000,
						boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
					}}>
					<Typo sx={{ color: "#fff", fontSize: "12px", fontWeight: "600", marginBottom: "8px" }}>{msg("statusHistory")}</Typo>
					<VBox gap="6px">
						{historyItems.slice(0, 5).map((item: any, index: number) => {
							const payload = parseClientStatusPayload(item.data);
							return (
								<HBox key={index} sx={{ alignItems: "center", gap: "6px" }}>
									<Typo sx={{ color: "#aaa", fontSize: "10px", whiteSpace: "nowrap" }}><DateTimeTooltip value={item.created_at}>{formatDisplayDate(item.created_at)}</DateTimeTooltip></Typo>
									<Typo sx={{ color: "#888", fontSize: "10px" }}>{payload.old_status ? createName(payload.old_status) : "—"}</Typo>
									<IconArrowRight />
									<Typo sx={{ color: "#fff", fontSize: "10px", fontWeight: "500" }}>{payload.new_status ? createName(payload.new_status) : "—"}</Typo>
								</HBox>
							);
						})}
					</VBox>
				</Box>
			)}
		</div>
	);
};

/**
 * Generates a deterministic gradient for a client header card. Visual goals:
 *   - In palette with the project's warm/muted admin aesthetic — no jarring lime/yellow,
 *     no muddy color combinations.
 *   - Each client gets a unique-feeling color (~14 base hues), but every output sits in
 *     the same saturation/lightness band → the whole UI keeps a consistent feel even when
 *     viewing many clients side by side.
 *   - Dark enough for white text overlay to stay legible (lightness band caps at ~34%).
 *
 * Gradient stops are *analogous* (the second hue is a small shift of the first) — this
 * reads as a polished single-tone gradient rather than two unrelated colors clashing.
 */
export const getMerchantGradient = (merchantId: string | number): string => {
	// Curated base hues. Skipped: ~50-110 (lime/olive yellows tend to look "dirty" when
	// desaturated) and pure red 0° (too aggressive on a card header).
	const baseHues = [
		210,  // dusty blue
		225,  // soft indigo
		245,  // muted periwinkle
		265,  // plum
		285,  // soft magenta
		330,  // dusty rose
		350,  // warm coral
		18,   // burnt orange
		35,   // amber
		145,  // sage green
		165,  // muted teal
		185,  // soft cyan
		195,  // dusty teal-blue
		275,  // mauve
	];

	// Deterministic 32-bit hash from the id string (djb2-like).
	const idStr = merchantId.toString();
	let hash = 5381;
	for (let i = 0; i < idStr.length; i++) {
		hash = ((hash << 5) + hash + idStr.charCodeAt(i)) & 0x7fffffff;
	}

	const baseHue = baseHues[hash % baseHues.length];
	const hueShift = 20 + ((hash >> 8) % 10);     // 20-29° analogous shift
	const sat1     = 30 + ((hash >> 16) % 8);     // 30-37% — muted, not vibrant
	const sat2     = sat1 + 5;                    // slightly more saturated on the highlight end
	const light1   = 24;                          // dark base for text contrast
	const light2   = 32;                          // slightly lighter highlight

	const c1 = `hsl(${baseHue}, ${sat1}%, ${light1}%)`;
	const c2 = `hsl(${(baseHue + hueShift) % 360}, ${sat2}%, ${light2}%)`;

	return `linear-gradient(55deg, ${c1} 0%, ${c2} 100%)`;
};
